import type { CreateLegalEntityInput, LegalEntity } from "../types/legalEntity";
import { formatPhoneForApiPayload } from "../utils/validation/legalEntityValidation";
import { apiRequest } from "./apiClient";

type BackendLegalEntityItem = {
  readonly id: string;
  readonly entityName?: string | null;
  readonly name?: string | null;
  readonly title?: string | null;
  readonly company?: string | null;
  readonly streetAddress?: string | null;
  readonly street?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly zip?: string | null;
  readonly address?: string | null;
  readonly contact_name?: string | null;
  readonly contact_title?: string | null;
  readonly plan_group_name?: string | null;
  readonly is_active?: boolean | null;
  readonly phone?: string | null;
  readonly email?: string | null;
};

type BackendLegalEntitiesResponse = {
  readonly data: BackendLegalEntityItem[];
  readonly meta?: unknown;
};

type BackendCreateLegalEntityResponse = {
  readonly data?: BackendLegalEntityItem;
};

export function mergeLegalEntityAddress(
  entity: Pick<LegalEntity, "street" | "city" | "state" | "zip" | "address">,
): string {
  const street = entity.street?.trim();
  const city = entity.city?.trim();
  const state = entity.state?.trim();
  const zip = entity.zip?.trim();
  const hasStructured = Boolean(street || city || state || zip);
  if (hasStructured) {
    const stateZip = [state, zip].filter(Boolean).join(" ");
    return [street, city, stateZip].filter(Boolean).join(", ");
  }
  const single = entity.address?.trim();
  return single ?? "";
}

/** Single-line "City, ST ZIP" for notice blocks (omits empty parts). */
export function formatLegalEntityCityStateZip(
  entity: Pick<LegalEntity, "city" | "state" | "zip">,
): string {
  const city = entity.city?.trim() ?? "";
  const state = entity.state?.trim() ?? "";
  const zip = entity.zip?.trim() ?? "";
  const stateZip = [state, zip].filter(Boolean).join(" ");
  if (city && stateZip) {
    return `${city}, ${stateZip}`;
  }
  if (city) {
    return city;
  }
  return stateZip;
}

const mapItem = (item: BackendLegalEntityItem): LegalEntity => {
  const displayName =
    (item.entityName ?? item.company ?? item.name ?? "").trim() || "Unknown";
  const street = (item.streetAddress ?? item.street ?? "").trim() || null;
  const city = (item.city ?? "").trim() || null;
  const state = (item.state ?? "").trim() || null;
  const zip = (item.zip ?? "").trim() || null;
  const addressLine = (item.address ?? "").trim() || null;
  const raw: LegalEntity = {
    id: item.id,
    name: displayName,
    address: addressLine,
    contactName: (item.name ?? item.contact_name ?? "").trim() || undefined,
    contactTitle: (item.title ?? item.contact_title ?? "").trim() || undefined,
    planGroupName: item.plan_group_name?.trim() || undefined,
    street,
    city,
    state,
    zip,
    phone: item.phone?.trim() || null,
    email: item.email?.trim() || null,
    isActive: item.is_active ?? undefined,
  };
  const merged = mergeLegalEntityAddress(raw);
  return {
    ...raw,
    address: merged.length > 0 ? merged : raw.address,
  };
};

export const listLegalEntities = async (): Promise<LegalEntity[]> => {
  const response = await apiRequest("/legal-entities?status=Active");
  const json = (await response.json()) as BackendLegalEntitiesResponse;
  if (!Array.isArray(json.data)) {
    return [];
  }

  return json.data.map(mapItem);
};

export const createLegalEntity = async (
  input: CreateLegalEntityInput,
): Promise<LegalEntity> => {
  const response = await apiRequest("/legal-entities", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: input.name,
      title: input.title,
      company: input.company,
      streetAddress: input.streetAddress,
      city: input.city,
      state: input.state,
      zip: input.zip,
      phone: input.phone.trim() ? formatPhoneForApiPayload(input.phone) : "",
      email: input.email,
    }),
  });

  const json = (await response.json()) as BackendCreateLegalEntityResponse;
  if (!json.data) {
    throw new Error("Failed to create legal entity");
  }
  return mapItem(json.data);
};
